import { Component, OnInit } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AdvSearchEvent } from '../../models/adv-search-event-model';
import { MetadataProp } from '../../models/metadata-prop-model';
import { SearchComboItemModel } from '../../models/search-combo-item-model';
import { SuggestRequestModel } from '../../models/suggest-request-model';
import { ConstantsService } from '../../services/constants.service';
import { FormsService } from '../../services/forms.service';
import { LoaderService } from '../../services/loader.service';
import { PubSearchService } from '../../services/pub-search.service';
import { QueryStateService } from '../../services/query-state.service';
import { environment } from '../../../environments/environment';
import { FoldersService } from '../../services/folders.service';
import { ResultSelectionService } from '../../services/result-selection.service';

@Component({
  selector: 'app-adv-search-view',
  templateUrl: './adv-search-view.component.html',
  styleUrls: ['./adv-search-view.component.css']
})
export class AdvSearchViewComponent implements OnInit {

  get canSubmit(): boolean {
    // Check keywords
    const kw = this.searchForm?.controls["keywords"]?.value || '';
    if (typeof kw === 'string' && kw.trim().length >= 3) {
      return true;
    }

    // Helper to check if a property has a value (text or date)
    const hasValue = (arr: FormArray) => {
      for (let i = 0; i < arr.length; i++) {
        const prop = arr.at(i).value;
        // If isDate, check for startDate or endDate
        if (prop.isDate) {
          if ((prop.startDate && prop.startDate.trim() !== '') || (prop.endDate && prop.endDate.trim() !== '')) {
            return true;
          }
        } else {
          // Otherwise, check keywords
          if (prop.keywords && prop.keywords.trim() !== '') {
            return true;
          }
        }
      }
      return false;
    };

    // Check All and Any property arrays
    const allProps = this.props(this.constants.property_operator_all);
    const anyProps = this.props(this.constants.property_operator_any);
    if (hasValue(allProps) || hasValue(anyProps)) {
      return true;
    }

    return false;
  }

  mdItems?: MetadataProp[];

  anyHeaderMsg = "The search query will use an OR operator between each of the properties in this tab. If ANY of these properties match the document it will be returned.";
  allHeaderMsg = "The search query will use an AND operator between each of the properties in this tab. The document MUST MATCH ALL THE PROPERTIES.";

  searchForm: FormGroup;
  spaceOperator: string = ' ';
  notOperator: string = '-';
  showCombined = false;
  aSugs: string[] = [];
  aFacetSugs: string[] = [];
  loading: boolean = true;
  loadSearchVisible = false;
  isLibSelectionValid = true;
  isProceedingSelectionValid = true;
  selectedLib: string;

  constructor(
    private formSvc: FormsService,
    private router: Router,
    private queryStateSvc: QueryStateService,
    private actRouter: ActivatedRoute,
    private titleSvc: Title,
    private loaderSvc: LoaderService,
    private searchSvc: PubSearchService,
    private msgSvc: MessageService,
    private folderSvc: FoldersService,
    public constants: ConstantsService,
    public selectionSvc: ResultSelectionService
  ) {
    this.titleSvc.setTitle(environment.site_name + ' - Adv Search');
    this.searchForm = this.formSvc.initSearchForm();
    this.selectedLib = this.constants.indexEHD;
  }

  ngOnInit(): void {
    this.subscribeLoader();
    this.actRouter.data.subscribe((d: any) => {
      this.mdItems = d.metadataProps;
    });

    this.folderSvc.selectedLibrary$.subscribe(l => {
      this.selectedLib = l;
      // console.log('Simple Search selected lib changed: ' + this.selectedLib);
    });

    this.addProp({ operatorType: this.constants.property_operator_all });
    this.addProp({ operatorType: this.constants.property_operator_any });
    this.initForm();
  }

  subscribeLoader() {
    this.loaderSvc.isLoading.subscribe((v) => {
      this.loading = v;
    });
  }

  initForm() {
    let fd = this.queryStateSvc.getFormData();

    if (fd) {

      let csAll = fd[this.constants.property_operator_all];
      let csAny = fd[this.constants.property_operator_any];

      if (csAll && csAll.length > 1) {
        for (let i = 0; i < csAll.length - 1; i++) {
          this.addProp({ "operatorType": this.constants.property_operator_all });
        }
      }

      if (csAny && csAny.length > 1) {
        for (let i = 0; i < csAny.length - 1; i++) {
          this.addProp({ "operatorType": this.constants.property_operator_any });
        }
      }

      this.searchForm.patchValue({
        keywords: fd['keywords'],
        indexId: fd['indexId'],
        [this.constants.property_operator_all]: fd[this.constants.property_operator_all],
        [this.constants.property_operator_any]: fd[this.constants.property_operator_any],
        openProceedings: fd['openProceedings'],
        closedProceedings: fd['closedProceedings']
      });
    }
  }

  getSuggestions(event: any) {
    // don't call if the length is greater than 100
    if (event.query.length > 2 && event.query.length < 100) {
      let suggestQuery: SuggestRequestModel = { q: event.query, top: 25, suggester: this.constants.azSuggester, indexId: this.selectedLib };
      this.searchSvc.getSuggestions(suggestQuery).subscribe((data: string[]) => {
        // console.log('suggestions: ' + JSON.stringify(data));
        this.aSugs = data;
      });
    }
  }

  clearKeyword() {
    this.searchForm.controls["keywords"].setValue('');
  }

  makeQuoted(value: string): string {
    return "\"" + value + "\"";
  }

  kwSuggestionSelected(value: any) {
    // add quotes to selected items for auto-complete.
    // console.log('Selected Autocomplete: ' + JSON.stringify(value.value));
    this.searchForm.controls["keywords"].setValue(this.makeQuoted(value.value));
    // this.searchForm.controls["keywords"].setValue(value);
  }

  getFacetSuggestions(event: any, mdItemId: string) {

    if (!mdItemId) {
      return;
    }

    let mdItem = this.mdItems?.find((m) => m.id == mdItemId) as MetadataProp;
    let propertyName = mdItem.cogSearchKey;

    let suggestQuery: SuggestRequestModel = { q: event.query, propertyName: propertyName, suggester: "n/a", indexId: this.selectedLib };
    this.searchSvc.getFacetSuggestions(suggestQuery).subscribe((data: string[]) => {
      this.aFacetSugs = data;
    });
  }

  handleKeywordDropDown(event: any) {

  }

  toggleCombined() {
    this.showCombined = !this.showCombined;
  }

  submitForm() {
    // Update the form values in the query state service which drives functions
    this.queryStateSvc.updateFormData(this.searchForm.value); // Will update/create the Search request in the Query Service.
    this.selectionSvc.clearAll();

    // console.log("query: " + JSON.stringify(this.queryStateSvc.getFormData()));

    this.router.navigate([
      '/results/' + encodeURIComponent(JSON.stringify(this.queryStateSvc.getFormData())),
    ]);
  }

  // validateCombinedSearch(cbItem: CombinedSearchFromModel): boolean {
  //   if (!cbItem.propertyItem) { return false; }

  //   //Check the type of the Property selected

  //   return true;
  // }

  clearSearch() {
    // this.showCombined = false;
    this.searchForm.controls["keywords"].setValue('');

    this.clearSearchProps(this.constants.property_operator_all);
    this.clearSearchProps(this.constants.property_operator_any);

    // console.log("sf: " + JSON.stringify(this.searchForm.value));
    this.queryStateSvc.updateFormData(this.searchForm.value);
  }

  clearSearchProps(operatorType: string) {
    let cs = this.searchForm.get(operatorType) as FormArray;
    while (cs.length !== 0) {
      cs.removeAt(0);
    }
    let np: AdvSearchEvent = {
      operatorType: operatorType
    }
    this.addProp(np);
  }

  props(operatorProp: string): FormArray {
    return this.searchForm.get(operatorProp) as FormArray;
  }

  newProp(): FormGroup {
    return this.formSvc.newProp();
  }

  updateProp(prop: SearchComboItemModel): FormGroup {
    return this.formSvc.updateProp(prop);
  }

  updateDateProp(prop: SearchComboItemModel): FormGroup {
    return this.formSvc.updateDateProp(prop);
  }

  pushProp(item: FormGroup, operatorType: string) {
    this.props(operatorType).push(item);
  }

  addProp(e: AdvSearchEvent) {
    const max_props = 10;
    if (e.operatorType) {
      if (this.props(e.operatorType).length < max_props) {
        this.props(e.operatorType).push(this.newProp());
      }
      else {
        // Show toast message stating max properties reached.
        this.msgSvc.add({ key: 'maxprops', severity: 'error', summary: 'The maximum number of properties (' + max_props + ') have been added for the operator "' + e.operatorType + '".' });
      }
    }
  }

  removeProp(e: AdvSearchEvent) {
    if (e.operatorType) {
      this.props(e.operatorType).removeAt(e.data);
    }
  }

  clearProp(e: AdvSearchEvent) {
    if (e.operatorType) {
      this.props(e.operatorType).removeAt(e.data);

      this.props(e.operatorType).insert(e.data, this.newProp());
    }
  }

  // toggleDateOp(e: AdvSearchEvent) {
  //   if (e.operatorType) {
  //     let csItem = this.props(e.operatorType).at(e.data).value;
  //     let dateOp = csItem.dateOperator;
  //   }
  // }

  selectedProp(e: AdvSearchEvent) {
    if (e.operatorType) {
      let csItem = this.props(e.operatorType).at(e.data).value;

      let md = csItem.propertyItem;
      let i = this.mdItems?.find((m) => m.id == md) as MetadataProp;

      if (i) {
        if (i.propertyType === this.constants.md_property_type_date) {
          this.props(e.operatorType).removeAt(e.data);
          this.props(e.operatorType).insert(e.data, this.updateDateProp(csItem));
        } else {
          this.props(e.operatorType).removeAt(e.data);
          this.props(e.operatorType).insert(e.data, this.updateProp(csItem));
        }
      }
    }
  }

  executeLoadSearch(query: any) {
    this.loadSearchVisible = false;
    this.queryStateSvc.updateFormData(query);
    this.router.navigate([
      '/results/' + encodeURIComponent(JSON.stringify(this.queryStateSvc.getFormData())),
    ]);
  }

  closeLoadDialog() {
    this.loadSearchVisible = false;
  }

  showLoadSearch() {
    this.loadSearchVisible = true;
  }

  validateLibraries() {
    const indexLib = this.searchForm.controls["indexId"].value;
    console.log('Lib selected: ' + indexLib);
    if (indexLib) {
      this.isLibSelectionValid = true;
      this.folderSvc.setSelectedLibrary(indexLib);
    }
    else {
      this.isLibSelectionValid = false;
    }
  }

  validateProceedingStatus() {
    if (this.searchForm.controls["openProceedings"].value || this.searchForm.controls["closedProceedings"].value) {
      this.isProceedingSelectionValid = true;
    }
    else {
      this.isProceedingSelectionValid = false;
    }
  }
}
